// ============================================================
// Método de los Lúmenes (CITCEA-UPC) — Motor de cálculo de alumbrado interior
// Fuente metodológica: CITCEA-UPC, "Cálculo de una instalación de alumbrado
// interior por el método de los lúmenes" (Universitat Politècnica de Catalunya).
// Marco normativo: Anexo 3 AM 196 — Norma Técnica en Seguridad e Higiene del
// Trabajo, Art. 10 "Iluminación" (ver docs/normativa/anexo3_seguridad_higiene.md).
//
// IMPORTANTE: los coeficientes de utilización usados aquí (UTILIZATION_FACTOR_TABLE)
// son valores ORIENTATIVOS tomados de las tablas genéricas de CITCEA para cada
// tipo de distribución fotométrica. NO sustituyen los datos fotométricos reales
// de la luminaria (curva de distribución / archivo IES del fabricante). Se deben
// usar solo quando no se dispone de esa información.
// ============================================================

// ===== Art. 10 punto 4 — Niveles mínimos de iluminación =====
export type TipoTareaArt10 =
  | 'tareas_generales_oficina'
  | 'mayor_atencion_detalle'
  | 'precision_extrema'
  | 'circulacion_pasillos';

export interface NivelIluminacionArt10 {
  label: string;
  min: number;
  max: number;
}

// Art. 10.4 del Anexo 3 AM 196: tabla de niveles mínimos de iluminación (lux)
// por área de trabajo / tarea.
export const EM_TABLE_ART10: Record<TipoTareaArt10, NivelIluminacionArt10> = {
  tareas_generales_oficina: {
    label: 'Tareas generales y actividades de oficina',
    min: 300,
    max: 300,
  },
  mayor_atencion_detalle: {
    label: 'Trabajos que requieren mayor atención al detalle (laboratorios, líneas de producción, etc.)',
    min: 750,
    max: 750,
  },
  precision_extrema: {
    label: 'Tareas que requieren precisión extrema (inspección de piezas finas, etc.)',
    min: 1500,
    max: 2000,
  },
  circulacion_pasillos: {
    label: 'Áreas de circulación y pasillos',
    min: 100,
    max: 200,
  },
};

// Nota INFORMATIVA y neutra: cumpleNormativa compara solo contra el mínimo
// del Art. 10.4 y el máximo no es un techo legal. Si la tarea tiene rango
// (max > min) y el diseño lo supera, se sugiere revisar deslumbramiento y
// consumo. null en tareas de valor único o dentro del rango.
export function notaSobreRangoArt10(iluminancia: number, nivel: NivelIluminacionArt10): string | null {
  if (!(nivel.max > nivel.min) || !(iluminancia > nivel.max)) return null;
  return `Por encima del rango recomendado (${nivel.min}–${nivel.max} lux): revisar deslumbramiento y consumo energético.`;
}

// ===== Tipos de entrada =====

export interface RoomInput {
  largo: number;              // a (m)
  ancho: number;               // b (m)
  altura: number;               // altura total del local (m)
  alturaPlanoTrabajo: number;  // altura del plano de trabajo sobre el piso (m)
}

export interface ReflectanceInput {
  techo: number;   // % (0-100)
  paredes: number; // % (0-100)
  piso: number;    // % (0-100)
}

export type TipoDistribucion =
  | 'directa'
  | 'semidirecta'
  | 'directa-indirecta'
  | 'indirecta'
  | 'semiindirecta';

export interface LuminaireInput {
  lumenesPorLampara: number;
  cantidadLamparasPorLuminaria: number;
  factorPerdidaLuz: number; // LLF / factor de depreciación de la lámpara (0-1)
  tipoDistribucion: TipoDistribucion;
  // CU (coeficiente de utilización) real tomado de la ficha fotométrica del
  // fabricante, si el usuario lo tiene. Cuando está definido, tiene prioridad
  // sobre la tabla orientativa de CITCEA-UPC (ver calculateUtilizationFactor).
  coeficienteUtilizacion?: number;
}

export type CalculationCriteria = 'iluminancia' | 'densidadPotencia' | 'cantidad';

// ===== Resultados intermedios / auxiliares =====

export interface SpacingResult {
  filas: number;
  columnas: number;
  espaciadoX: number;         // separación entre luminarias en el eje X (m)
  espaciadoY: number;         // separación entre luminarias en el eje Y (m)
  // Distancia de la primera columna (X) / primera fila (Y) a su muro (m).
  // Centra la grilla en cada eje: el margen es igual en ambos lados.
  desplazamientoParedX: number;
  desplazamientoParedY: number;
  // LEGADO — solo existe en los cálculos guardados en lighting_calculations
  // antes de 2026-09-25, cuando se usaba min(espaciadoX, espaciadoY)/2 para
  // AMBOS ejes (grilla descentrada si espaciadoX ≠ espaciadoY). No se
  // escribe en cálculos nuevos; se conserva en el tipo para poder redibujar
  // esas filas antiguas exactamente como el usuario las vio y guardó (ver
  // wallOffsets()).
  desplazamientoPared?: number;
}

// Distancia a pared por eje de una SpacingResult, nueva o guardada: usa
// desplazamientoParedX/Y y, en filas antiguas que no los tienen, el
// desplazamientoPared único con el que se guardaron.
export function wallOffsets(espaciado: SpacingResult): { x: number; y: number } {
  const legacy = espaciado.desplazamientoPared ?? 0;
  return {
    x: espaciado.desplazamientoParedX ?? legacy,
    y: espaciado.desplazamientoParedY ?? legacy,
  };
}

// Posiciones (m) de la grilla filas × columnas, en orden fila-mayor (el
// índice es el que usan las posiciones manuales customPositions). ÚNICA
// fuente de la geometría de la grilla: la usan la planta 2D, la vista 3D,
// el PDF y el cálculo de uniformidad (ies-photometric.ts).
export function luminaireGridPositions(espaciado: SpacingResult): { x: number; y: number }[] {
  if (espaciado.filas <= 0 || espaciado.columnas <= 0) return [];
  const off = wallOffsets(espaciado);
  const positions: { x: number; y: number }[] = [];
  for (let fila = 0; fila < espaciado.filas; fila++) {
    for (let columna = 0; columna < espaciado.columnas; columna++) {
      positions.push({ x: off.x + columna * espaciado.espaciadoX, y: off.y + fila * espaciado.espaciadoY });
    }
  }
  return positions;
}

export interface ZonalCavityResult {
  alturaCavidadLocal: number;   // hrc: luminaria -> plano de trabajo
  alturaCavidadTecho: number;   // hcc: techo -> luminaria
  alturaCavidadPiso: number;    // hfc: plano de trabajo -> piso
  cavityRatioLocal: number;     // RCR
  cavityRatioTecho: number;     // CCR
  cavityRatioPiso: number;      // FCR
  reflectanciaEfectivaTecho: number; // % — reflectancia efectiva de la cavidad de techo
  reflectanciaEfectivaPiso: number;  // % — reflectancia efectiva de la cavidad de piso
}

export interface IluminacionFullReport {
  tipoTarea: TipoTareaArt10;
  emRequerido: NivelIluminacionArt10;
  emDiseno: number; // lux objetivo usado para el dimensionamiento (promedio del rango Art. 10)
  area: number;
  indiceLocal: number;
  factorUtilizacion: number;
  factorMantenimiento: number;
  flujoLuminosoTotal: number;
  numeroLuminarias: number;
  numeroLuminariasInstaladas: number; // = filas × columnas, grilla completa uniforme (prioridad sobre el mínimo teórico numeroLuminarias, que se mantiene solo para trazabilidad normativa)
  espaciado: SpacingResult;
  cavidadZonal: ZonalCavityResult;
  iluminanciaResultante: number; // lux realmente entregados con el nº de luminarias redondeado
  cumpleNormativa: boolean;
  // Indica si factorUtilizacion proviene del CU de fabricante ('fabricante')
  // o de la tabla orientativa de CITCEA-UPC ('estimado'). La UI/PDF deben
  // mostrar la advertencia de "valor orientativo sin verificar" cuando sea 'estimado'.
  fuenteFactorUtilizacion: 'fabricante' | 'estimado';
}

// ===== 1. Índice del local (k) =====
// CITCEA-UPC, método de los lúmenes:
//  - Iluminación directa, semidirecta, directa-indirecta y general difusa:
//      k = (a · b) / (h · (a + b))
//  - Iluminación indirecta y semiindirecta:
//      k = 3 · (a · b) / (2 · h · (a + b))
// donde a = largo, b = ancho, h = altura de montaje de la luminaria sobre el
// plano de trabajo.
export function calculateRoomIndex(
  room: RoomInput,
  luminaireHeight: number,
  tipoDistribucion: TipoDistribucion
): number {
  const { largo: a, ancho: b } = room;
  const h = luminaireHeight;
  if (h <= 0) return 0;

  const base = (a * b) / (h * (a + b));

  if (tipoDistribucion === 'indirecta' || tipoDistribucion === 'semiindirecta') {
    return (3 / 2) * base;
  }
  return base;
}

// ===== 2. Factor de utilización (Fu) =====
// CITCEA-UPC: tabla orientativa del coeficiente de utilización por tipo de
// distribución fotométrica, tomando como referencia reflectancias altas
// (techo 70%, paredes 50%, piso 30% — combinación habitual "7-5-3" de las
// tablas de fabricante). Se interpola en función del índice del local (k) y
// se corrige por la reflectancia promedio real del recinto respecto a la
// referencia. Esto es una APROXIMACIÓN orientativa: en un proyecto real debe
// sustituirse por la tabla fotométrica específica de la luminaria (dato de
// fabricante / archivo IES).
const UTILIZATION_FACTOR_TABLE: Record<TipoDistribucion, { k: number; fu: number }[]> = {
  directa: [
    { k: 0.6, fu: 0.40 }, { k: 0.8, fu: 0.48 }, { k: 1, fu: 0.55 },
    { k: 1.25, fu: 0.61 }, { k: 1.5, fu: 0.65 }, { k: 2, fu: 0.71 },
    { k: 2.5, fu: 0.75 }, { k: 3, fu: 0.79 }, { k: 4, fu: 0.83 }, { k: 5, fu: 0.87 },
  ],
  semidirecta: [
    { k: 0.6, fu: 0.34 }, { k: 0.8, fu: 0.42 }, { k: 1, fu: 0.48 },
    { k: 1.25, fu: 0.54 }, { k: 1.5, fu: 0.58 }, { k: 2, fu: 0.64 },
    { k: 2.5, fu: 0.68 }, { k: 3, fu: 0.71 }, { k: 4, fu: 0.76 }, { k: 5, fu: 0.79 },
  ],
  'directa-indirecta': [
    { k: 0.6, fu: 0.24 }, { k: 0.8, fu: 0.31 }, { k: 1, fu: 0.36 },
    { k: 1.25, fu: 0.42 }, { k: 1.5, fu: 0.46 }, { k: 2, fu: 0.52 },
    { k: 2.5, fu: 0.57 }, { k: 3, fu: 0.60 }, { k: 4, fu: 0.66 }, { k: 5, fu: 0.70 },
  ],
  semiindirecta: [
    { k: 0.6, fu: 0.18 }, { k: 0.8, fu: 0.24 }, { k: 1, fu: 0.29 },
    { k: 1.25, fu: 0.34 }, { k: 1.5, fu: 0.38 }, { k: 2, fu: 0.44 },
    { k: 2.5, fu: 0.48 }, { k: 3, fu: 0.52 }, { k: 4, fu: 0.57 }, { k: 5, fu: 0.61 },
  ],
  indirecta: [
    { k: 0.6, fu: 0.12 }, { k: 0.8, fu: 0.17 }, { k: 1, fu: 0.21 },
    { k: 1.25, fu: 0.25 }, { k: 1.5, fu: 0.29 }, { k: 2, fu: 0.34 },
    { k: 2.5, fu: 0.38 }, { k: 3, fu: 0.41 }, { k: 4, fu: 0.46 }, { k: 5, fu: 0.50 },
  ],
};

// Reflectancia de referencia de la tabla anterior (techo 70% / paredes 50% / piso 30%)
const REFERENCE_REFLECTANCE_AVG = (70 + 50 + 30) / 3;

function interpolateUtilizationFactor(k: number, points: { k: number; fu: number }[]): number {
  if (k <= points[0].k) return points[0].fu;
  const last = points[points.length - 1];
  if (k >= last.k) return last.fu;

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    if (k >= p0.k && k <= p1.k) {
      const t = (k - p0.k) / (p1.k - p0.k);
      return p0.fu + t * (p1.fu - p0.fu);
    }
  }
  return last.fu;
}

export function calculateUtilizationFactor(
  k: number,
  reflectances: ReflectanceInput,
  tipoDistribucion: TipoDistribucion,
  coeficienteUtilizacion?: number
): number {
  // Si el usuario aporta el CU real de la ficha fotométrica del fabricante,
  // se usa directamente y se ignora la tabla orientativa de CITCEA-UPC.
  if (coeficienteUtilizacion !== undefined) {
    return coeficienteUtilizacion;
  }

  const puntos = UTILIZATION_FACTOR_TABLE[tipoDistribucion];
  const fuBase = interpolateUtilizationFactor(k, puntos);

  // Corrección lineal simple por reflectancia promedio real vs. la referencia
  // de la tabla (aproximación orientativa, no dato de fabricante).
  const reflectanciaPromedio =
    (reflectances.techo + reflectances.paredes + reflectances.piso) / 3;
  const factorCorreccion = reflectanciaPromedio / REFERENCE_REFLECTANCE_AVG;

  const fuCorregido = fuBase * factorCorreccion;
  // El factor de utilización nunca puede superar 1 ni ser negativo.
  return Math.min(1, Math.max(0, fuCorregido));
}

// ===== 3. Factor de mantenimiento (Fm) =====
// CITCEA-UPC: factor de conservación / mantenimiento según limpieza del
// ambiente de trabajo: 0.8 en ambientes limpios, 0.6 en ambientes sucios.
export function calculateMaintenanceFactor(ambiente: 'limpio' | 'sucio'): number {
  return ambiente === 'limpio' ? 0.8 : 0.6;
}

// ===== 4. Flujo luminoso total necesario =====
// CITCEA-UPC, método de los lúmenes:
//   Φtotal = (Em · S) / (Fu · Fm)
// donde Em = iluminancia media requerida (lux, Art. 10.4), S = superficie del
// local (m²), Fu = factor de utilización, Fm = factor de mantenimiento.
export function calculateTotalLumens(
  area: number,
  em: number,
  utilizationFactor: number,
  maintenanceFactor: number
): number {
  if (utilizationFactor <= 0 || maintenanceFactor <= 0) return 0;
  return (em * area) / (utilizationFactor * maintenanceFactor);
}

// ===== 5. Número de luminarias =====
// N = Φtotal / (lumenesPorLampara · cantidadLamparasPorLuminaria · factorPerdidaLuz),
// redondeado por exceso: no se puede instalar una fracción de luminaria y el
// diseño debe garantizar como mínimo el Em exigido por el Art. 10.
export function calculateNumberOfLuminaires(
  totalLumens: number,
  luminaire: LuminaireInput
): number {
  const lumenesEfectivosPorLuminaria =
    luminaire.lumenesPorLampara *
    luminaire.cantidadLamparasPorLuminaria *
    luminaire.factorPerdidaLuz;

  if (lumenesEfectivosPorLuminaria <= 0) return 0;
  return Math.ceil(totalLumens / lumenesEfectivosPorLuminaria);
}

// ===== 6. Distribución / espaciado de luminarias =====
// CITCEA-UPC: reparto de luminarias en una grilla rectangular proporcional a
// las dimensiones del local, con la primera fila/columna desplazada medio
// espaciado desde la pared (regla práctica habitual del método de los lúmenes
// para lograr uniformidad de iluminación en los bordes del recinto).
export function calculateSpacing(room: RoomInput, numLuminaires: number): SpacingResult {
  if (numLuminaires <= 0) {
    return { filas: 0, columnas: 0, espaciadoX: 0, espaciadoY: 0, desplazamientoParedX: 0, desplazamientoParedY: 0 };
  }

  const { largo, ancho } = room;

  // Reparto proporcional a la forma del local, calculado sobre (lado mayor,
  // lado menor) y luego asignado a filas (eje Y = largo) y columnas (eje X =
  // ancho): girar el local da el mismo total y la grilla transpuesta. Antes se
  // calculaba sobre largo/ancho (columnas con round, filas con ceil) y el total
  // cambiaba al girar (10×40 → 40; 40×10 → 39).
  const mayor = Math.max(largo, ancho);
  const menor = Math.min(largo, ancho);
  const enMenorInicial = Math.max(1, Math.round(Math.sqrt((numLuminaires * menor) / mayor)));
  const enMayor = Math.ceil(numLuminaires / enMenorInicial);
  // Se quitan posiciones sobrantes del lado menor mientras se cubra numLuminaires.
  const enMenor = Math.ceil(numLuminaires / enMayor);
  const filas = largo >= ancho ? enMayor : enMenor;
  const columnas = largo >= ancho ? enMenor : enMayor;

  const espaciadoX = ancho / columnas;
  const espaciadoY = largo / filas;
  // Margen igual a ambos lados en cada eje: (dimensión − tramo ocupado por
  // la grilla) / 2. Con espaciado = dimensión / n, esto es exactamente
  // medio espaciado de ESE eje (la regla CITCEA de arriba, aplicada por
  // eje). Antes se usaba min(espaciadoX, espaciadoY)/2 en ambos ejes, lo que
  // descentraba la grilla en el eje de espaciado mayor.
  const desplazamientoParedX = (ancho - (columnas - 1) * espaciadoX) / 2;
  const desplazamientoParedY = (largo - (filas - 1) * espaciadoY) / 2;

  return { filas, columnas, espaciadoX, espaciadoY, desplazamientoParedX, desplazamientoParedY };
}

// ===== 7. Cavidad zonal (panel de detalle técnico) =====
// Método de la cavidad zonal (IES / CITCEA): descompone el local en tres
// cavidades (techo, local y piso) para caracterizar su geometría e influencia
// en la reflectancia efectiva.
//   Cavity Ratio = 5 · h_cavidad · (a + b) / (a · b)
// La reflectancia efectiva de las cavidades de techo/piso se aproxima aquí
// como el promedio ponderado por área entre la superficie base (techo o piso)
// y las paredes de la cavidad — simplificación razonable cuando no se dispone
// de las tablas iterativas completas del método IES.
export function calculateZonalCavity(
  room: RoomInput,
  luminaireHeight: number,
  reflectances: ReflectanceInput
): ZonalCavityResult {
  const { largo: a, ancho: b, altura, alturaPlanoTrabajo } = room;
  const areaBase = a * b;
  const perimetro = 2 * (a + b);

  const alturaCavidadLocal = luminaireHeight; // hrc: luminaria -> plano de trabajo
  const alturaCavidadTecho = Math.max(0, altura - alturaPlanoTrabajo - luminaireHeight); // hcc: techo -> luminaria
  const alturaCavidadPiso = alturaPlanoTrabajo; // hfc: plano de trabajo -> piso

  const cavityRatio = (h: number) => (areaBase > 0 ? (5 * h * (a + b)) / areaBase : 0);

  const cavityRatioLocal = cavityRatio(alturaCavidadLocal);
  const cavityRatioTecho = cavityRatio(alturaCavidadTecho);
  const cavityRatioPiso = cavityRatio(alturaCavidadPiso);

  const reflectanciaEfectiva = (reflectanciaBase: number, hCavidad: number): number => {
    const areaParedesCavidad = perimetro * hCavidad;
    const areaTotal = areaBase + areaParedesCavidad;
    if (areaTotal <= 0) return reflectanciaBase;
    // Promedio ponderado por área entre la base y las paredes de la cavidad
    // (paredes asumidas con la reflectancia de "paredes" del recinto).
    return (
      (reflectanciaBase * areaBase + reflectances.paredes * areaParedesCavidad) / areaTotal
    );
  };

  return {
    alturaCavidadLocal,
    alturaCavidadTecho,
    alturaCavidadPiso,
    cavityRatioLocal,
    cavityRatioTecho,
    cavityRatioPiso,
    reflectanciaEfectivaTecho: reflectanciaEfectiva(reflectances.techo, alturaCavidadTecho),
    reflectanciaEfectivaPiso: reflectanciaEfectiva(reflectances.piso, alturaCavidadPiso),
  };
}

// ===== 8. Reporte completo =====
// Orquesta el método de los lúmenes de punta a punta y evalúa el cumplimiento
// del Art. 10.4 del Anexo 3 AM 196 comparando la iluminancia resultante del
// diseño (con el nº de luminarias redondeado por exceso) contra el mínimo
// normativo exigido para el tipo de tarea seleccionado.
export function calculateFullReport(
  room: RoomInput,
  reflectances: ReflectanceInput,
  luminaire: LuminaireInput,
  tipoTarea: TipoTareaArt10,
  ambiente: 'limpio' | 'sucio'
): IluminacionFullReport {
  const emRequerido = EM_TABLE_ART10[tipoTarea];
  // Se diseña con el promedio del rango Art. 10 (min === max cuando el
  // artículo define un único valor, p.ej. oficinas: 300-300).
  const emDiseno = (emRequerido.min + emRequerido.max) / 2;

  const area = room.largo * room.ancho;
  const luminaireHeight = Math.max(0, room.altura - room.alturaPlanoTrabajo);

  const indiceLocal = calculateRoomIndex(room, luminaireHeight, luminaire.tipoDistribucion);
  const factorUtilizacion = calculateUtilizationFactor(
    indiceLocal,
    reflectances,
    luminaire.tipoDistribucion,
    luminaire.coeficienteUtilizacion
  );
  const fuenteFactorUtilizacion: 'fabricante' | 'estimado' =
    luminaire.coeficienteUtilizacion !== undefined ? 'fabricante' : 'estimado';
  const factorMantenimiento = calculateMaintenanceFactor(ambiente);

  const flujoLuminosoTotal = calculateTotalLumens(
    area,
    emDiseno,
    factorUtilizacion,
    factorMantenimiento
  );

  const numeroLuminarias = calculateNumberOfLuminaires(flujoLuminosoTotal, luminaire);
  const espaciado = calculateSpacing(room, numeroLuminarias);
  const cavidadZonal = calculateZonalCavity(room, luminaireHeight, reflectances);

  // La grilla filas×columnas de calculateSpacing() puede redondear a una
  // cantidad de posiciones mayor que el mínimo teórico numeroLuminarias (para
  // mantener una grilla uniforme) — numeroLuminariasInstaladas refleja la
  // cantidad real que se instala y sobre la que se recalcula la iluminancia
  // resultante, ya que instalar más luminarias que el mínimo mejora el
  // resultado real y sería incorrecto reportar solo el mínimo teórico.
  const numeroLuminariasInstaladas = espaciado.filas * espaciado.columnas;

  // Iluminancia realmente entregada con el nº de luminarias efectivamente
  // instaladas (grilla completa), despejando Em de la fórmula del método de
  // los lúmenes:
  //   Em = (N · lumenesPorLuminaria · Fu · Fm) / S
  const lumenesEfectivosPorLuminaria =
    luminaire.lumenesPorLampara * luminaire.cantidadLamparasPorLuminaria * luminaire.factorPerdidaLuz;
  const iluminanciaResultante =
    area > 0
      ? (numeroLuminariasInstaladas * lumenesEfectivosPorLuminaria * factorUtilizacion * factorMantenimiento) / area
      : 0;

  // Se compara solo contra el mínimo del Art. 10.4: el máximo del rango
  // (p.ej. 1500-2000 lux en precisión extrema) se interpreta como una guía
  // orientativa de diseño, no como un techo cuyo exceso constituya
  // incumplimiento normativo.
  //
  // Respaldo: el propio Art. 10.4 del Anexo 3 se titula "Niveles mínimos de
  // Iluminación" y su tabla indica "Nivel mínimo de iluminación (Lux)"; no
  // fija un techo. (El DE 2393, que antes se citaba aquí, NO está vigente:
  // la Disposición Transitoria Décima Segunda del DE 255 lo mantuvo solo
  // hasta que el MDT emitiera la normativa técnica, el AM 196 (2024).
  // Verificado el 28/09/2026.)
  //
  // Uniformidad: el Anexo 3 no fija un valor (Art. 10.1.c.iii solo pide "una
  // distribución uniforme de la luz"). Por el DE 255 Art. 48 se usa la
  // EN 12464-1:2021 (Uo = Emín/Epromedio) como indicador INFORMATIVO,
  // calculado aparte con estimateDirectUniformity() (src/lib/ies-photometric.ts),
  // y NO entra en cumpleNormativa (decisión con Kevin, 24/09/2026): solo
  // considera la luz directa, así que subestima la uniformidad real.
  const cumpleNormativa = iluminanciaResultante >= emRequerido.min;

  return {
    tipoTarea,
    emRequerido,
    emDiseno,
    area,
    indiceLocal,
    factorUtilizacion,
    factorMantenimiento,
    flujoLuminosoTotal,
    numeroLuminarias,
    numeroLuminariasInstaladas,
    espaciado,
    cavidadZonal,
    iluminanciaResultante,
    cumpleNormativa,
    fuenteFactorUtilizacion,
  };
}
